import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  BuildingOfficeIcon, 
  ArchiveBoxIcon, 
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTypeContext } from '../contexts/BusinessTypeContext';
import { getOnboardingChecklist, useDashboard } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import apiClient from '../utils/api';
import { getTaskStats } from '../utils/api';
import { OnboardingChecklistResponse } from '../types/api';

const StatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ComponentType<any>;
  color: string;
}> = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <div className="flex items-center">
      <div className={`flex-shrink-0 p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { data: dashboardData, loading, error } = useDashboard(selectedLocation?.id || null);
  const { user } = useAuth();
  const { businessType } = useBusinessTypeContext();

  // Compliance mini-report state (last 7 days)
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState<string>('');
  const [compRows, setCompRows] = useState<any[]>([]);

  // Task stats state
  const [taskStats, setTaskStats] = useState<{ pending_count: number; overdue_count: number; completed_pending_approval: number } | null>(null);
  const [checklist, setChecklist] = useState<OnboardingChecklistResponse | null>(null);
  const [fishMasterReady, setFishMasterReady] = useState<boolean | null>(null);
  const [goatMasterReady, setGoatMasterReady] = useState<boolean | null>(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState('');

  useEffect(() => {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 6 * 86400000).toISOString().slice(0, 10);
    setStart(from);
    setEnd(to);
  }, []);

  const fetchCompliance = async () => {
    if (!selectedLocation || !start || !end) return;
    setCompLoading(true);
    setCompError('');
    try {
      const res = await apiClient.get('/reports/compliance', {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
        },
      });
      setCompRows(res.data || []);
    } catch (e: any) {
      setCompError(e?.response?.data?.detail || 'Failed to load compliance');
      setCompRows([]);
    } finally {
      setCompLoading(false);
    }
  };

  useEffect(() => {
    fetchCompliance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, start, end]);

  const loadChecklist = async () => {
    if (!selectedLocation?.id) {
      setChecklist(null);
      return;
    }
    try {
      setChecklistLoading(true);
      setChecklistError('');
      const data = await getOnboardingChecklist(selectedLocation.id, businessType);
      setChecklist(data);

      const hasFish = (selectedLocation.business_types || []).includes('fish');
      const hasGoat = (selectedLocation.business_types || []).includes('goat');
      if (hasFish && hasGoat) {
        const [fishData, goatData] = await Promise.all([
          getOnboardingChecklist(selectedLocation.id, 'fish'),
          getOnboardingChecklist(selectedLocation.id, 'goat'),
        ]);
        setFishMasterReady(!!fishData.master_data_ready);
        setGoatMasterReady(!!goatData.master_data_ready);
      } else if (hasFish) {
        setFishMasterReady(!!data.master_data_ready);
        setGoatMasterReady(null);
      } else if (hasGoat) {
        setFishMasterReady(null);
        setGoatMasterReady(!!data.master_data_ready);
      } else {
        setFishMasterReady(null);
        setGoatMasterReady(null);
      }
    } catch (e: any) {
      setChecklistError(e?.response?.data?.detail || e?.message || 'Failed to load onboarding checklist');
      setChecklist(null);
      setFishMasterReady(null);
      setGoatMasterReady(null);
    } finally {
      setChecklistLoading(false);
    }
  };

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, businessType]);

  // Load task stats
  useEffect(() => {
    if (selectedLocation?.id && (user?.role === 'admin' || user?.role === 'manager')) {
      loadTaskStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, user?.role]);

  const loadTaskStats = async () => {
    if (!selectedLocation?.id) return;
    try {
      const stats = await getTaskStats(selectedLocation.id);
      setTaskStats(stats);
    } catch (error) {
      console.error('Error loading task stats:', error);
    }
  };

  const OnboardingChecklistCard: React.FC = () => {
    if (!selectedLocation) return null;

    if (checklistLoading) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Guided Checklist</h2>
          <div className="flex justify-center py-2"><LoadingSpinner /></div>
        </div>
      );
    }

    if (!checklist || checklist.percent_complete >= 100) return null;

    const actionLinks = {
      setupStructure: businessType === 'goat' ? '/goat/sheds/new' : '/admin/locations',
      feed: businessType === 'goat' ? '/goat/data-entry/feed' : '/data-entry/feed',
      expense: businessType === 'goat' ? '/goat/data-entry/expense' : '/data-entry/expense',
      income: businessType === 'goat' ? '/goat/data-entry/income' : '/data-entry/income',
    };
    const hasFish = (selectedLocation.business_types || []).includes('fish');
    const hasGoat = (selectedLocation.business_types || []).includes('goat');
    const hasBoth = hasFish && hasGoat;

    const Item: React.FC<{ done: boolean; label: string; action?: React.ReactNode }> = ({ done, label, action }) => (
      <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
            done ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {done ? '✓' : '!'}
          </span>
          <span className="text-sm text-gray-800">{label}</span>
        </div>
        {action}
      </div>
    );

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Guided Checklist</h2>
          <span className="text-sm font-medium text-gray-600">{checklist.percent_complete}% complete</span>
        </div>

        <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${Math.max(0, Math.min(100, checklist.percent_complete))}%` }}
          />
        </div>
        {checklistError && (
          <div className="mb-3 text-sm text-red-600">{checklistError}</div>
        )}

        <div className="space-y-0">
          {hasBoth ? (
            <>
              <Item
                done={fishMasterReady === true}
                label="Fish master data ready"
                action={fishMasterReady !== true && user?.role === 'admin' ? <Link to="/admin/master-data" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
              />
              <Item
                done={goatMasterReady === true}
                label="Goat master data ready"
                action={goatMasterReady !== true && user?.role === 'admin' ? <Link to="/admin/master-data" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
              />
            </>
          ) : (
            <Item
              done={checklist.master_data_ready}
              label="Master data ready"
              action={!checklist.master_data_ready && user?.role === 'admin' ? <Link to="/admin/master-data" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
            />
          )}
          <Item
            done={checklist.has_pond_or_shed}
            label="Add first pond/shed"
            action={!checklist.has_pond_or_shed ? <Link to={actionLinks.setupStructure} className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_first_feed_log}
            label="Add first feed log"
            action={!checklist.has_first_feed_log ? <Link to={actionLinks.feed} className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_first_expense}
            label="Add first expense"
            action={!checklist.has_first_expense ? <Link to={actionLinks.expense} className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_first_income}
            label="Add first income"
            action={!checklist.has_first_income ? <Link to={actionLinks.income} className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
        </div>
      </div>
    );
  };

  // Group and sort recent-first
  const groupedByDate = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    for (const r of compRows) {
      const key = r.date;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(r);
    }
    Object.keys(grouped).forEach((d) => {
      grouped[d].sort((a, b) => (a.pond_name || '').localeCompare(b.pond_name || ''));
    });
    return grouped;
  }, [compRows]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  }, [groupedByDate]);

  // Reusable Recent Activity (Compliance) box
  const ComplianceRecentBox: React.FC = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
      {compError && (
        <div className="mb-3 text-sm text-red-600">{compError}</div>
      )}
      {compLoading ? (
        <div className="flex justify-center py-4"><LoadingSpinner /></div>
      ) : (
        <div className="space-y-3">
          {sortedDates.length === 0 && (
            <div className="text-sm text-gray-500">No data for last 7 days</div>
          )}
          {sortedDates.map((date) => {
            const group = groupedByDate[date] || [];
            const totalMissing = group.reduce((sum, r) => sum + (Number(r.missing_count) || 0), 0);
            const byName = (names: string[]) => names.filter(Boolean).sort((a, b) => a.localeCompare(b));

            const feedNo = byName(group.filter((r) => !r.feed_ok).map((r) => r.pond_name));
            const feedYes = byName(group.filter((r) => !!r.feed_ok).map((r) => r.pond_name));
            const waterNo = byName(group.filter((r) => !r.water_quality_ok).map((r) => r.pond_name));
            const waterYes = byName(group.filter((r) => !!r.water_quality_ok).map((r) => r.pond_name));
            const medNo = byName(group.filter((r) => !r.medicine_present).map((r) => r.pond_name));
            const medYes = byName(group.filter((r) => !!r.medicine_present).map((r) => r.pond_name));

            const BadgeRow = ({ items, color }: { items: string[]; color: 'green' | 'red' }) => {
              if (items.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1">
                  {items.map((name) => (
                    <span
                      key={name}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        color === 'green' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              );
            };

            return (
              <div key={date} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">{date}</div>
                  <div className="text-xs text-gray-600">Missing total: {totalMissing}</div>
                </div>
                <div className="p-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="bg-white border rounded-md p-2">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Feed</div>
                    <BadgeRow items={feedNo} color="red" />
                    <BadgeRow items={feedYes} color="green" />
                  </div>
                  <div className="bg-white border rounded-md p-2">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Water Quality</div>
                    <BadgeRow items={waterNo} color="red" />
                    <BadgeRow items={waterYes} color="green" />
                  </div>
                  <div className="bg-white border rounded-md p-2">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Medicine Present</div>
                    <BadgeRow items={medNo} color="red" />
                    <BadgeRow items={medYes} color="green" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-mobile py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading dashboard: {error}</p>
        </div>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Location Selected</h3>
          <p className="text-gray-600">Please select a farm location to view the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">🐟 {selectedLocation.name}</h1>
        <p className="text-blue-100 mt-1">FarmLog.in Dashboard</p>
      </div>

      <OnboardingChecklistCard />

      {/* Mobile-first: Recent Activity on top */}
      <div className="sm:hidden">
        <ComplianceRecentBox />
      </div>

      {/* Stats Grid */}
      {dashboardData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Ponds"
            value={dashboardData.total_ponds || 0}
            icon={ChartBarIcon}
            color="bg-blue-600"
          />
          <StatCard
            title="Active Cycles"
            value={dashboardData.active_cycles || 0}
            icon={BuildingOfficeIcon}
            color="bg-green-600"
          />
          <StatCard
            title="Total Biomass"
            value={`${(dashboardData.total_biomass_kg || 0).toFixed(1)} kg`}
            icon={ArchiveBoxIcon}
            color="bg-purple-600"
          />
          {user?.role === 'admin' && (
            <StatCard
              title="Monthly P&L"
              value={`₹${((dashboardData.profit_loss || 0) / 1000).toFixed(1)}K`}
              icon={ExclamationTriangleIcon}
              color={dashboardData.profit_loss >= 0 ? "bg-green-600" : "bg-red-600"}
            />
          )}
        </div>
      )}

      {/* Task Stats Widgets */}
      {(user?.role === 'admin' || user?.role === 'manager') && taskStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/tasks?status=pending">
            <StatCard
              title="Pending Tasks"
              value={taskStats.pending_count}
              icon={ClipboardDocumentCheckIcon}
              color="bg-yellow-600"
            />
          </Link>
          <Link to="/tasks?status=completed">
            <StatCard
              title={user?.role === 'admin' ? 'Pending Approval' : 'Completed'}
              value={user?.role === 'admin' ? taskStats.completed_pending_approval : taskStats.pending_count}
              icon={ClockIcon}
              color="bg-blue-600"
            />
          </Link>
          <Link to="/tasks">
            <StatCard
              title="Overdue Tasks"
              value={taskStats.overdue_count}
              icon={ExclamationTriangleIcon}
              color="bg-red-600"
            />
          </Link>
        </div>
      )}

      {/* Quick Actions - hidden for read-only users */}
      {user?.role !== 'user' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Link 
              to="/data-entry/feed" 
              className="p-4 bg-green-50 rounded-lg text-center hover:bg-green-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🌾</div>
              <div className="text-sm font-medium text-green-900">Feed Log</div>
            </Link>
            <Link 
              to="/data-entry/water-quality" 
              className="p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💧</div>
              <div className="text-sm font-medium text-blue-900">Water Quality</div>
            </Link>
            <Link 
              to="/data-entry/medicine" 
              className="p-4 bg-red-50 rounded-lg text-center hover:bg-red-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💊</div>
              <div className="text-sm font-medium text-red-900">Medicine Log</div>
            </Link>
            <Link 
              to="/data-entry/sampling" 
              className="p-4 bg-purple-50 rounded-lg text-center hover:bg-purple-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📊</div>
              <div className="text-sm font-medium text-purple-900">Fish Sampling</div>
            </Link>
            <Link 
              to="/data-entry/fish-seed" 
              className="p-4 bg-cyan-50 rounded-lg text-center hover:bg-cyan-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🐠</div>
              <div className="text-sm font-medium text-cyan-900">Fish Seed</div>
            </Link>
            <Link 
              to="/data-entry/mortality" 
              className="p-4 bg-gray-50 rounded-lg text-center hover:bg-gray-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💀</div>
              <div className="text-sm font-medium text-gray-900">Dead Fish</div>
            </Link>
            <Link 
              to="/data-entry/expense" 
              className="p-4 bg-orange-50 rounded-lg text-center hover:bg-orange-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💰</div>
              <div className="text-sm font-medium text-orange-900">Expense</div>
            </Link>
            <Link 
              to="/data-entry/income" 
              className="p-4 bg-emerald-50 rounded-lg text-center hover:bg-emerald-100 transition-colors group"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💵</div>
              <div className="text-sm font-medium text-emerald-900">Income</div>
            </Link>
          </div>
        </div>
      )}

      {/* Desktop/Tablet: Recent Activity in original position */}
      <div className="hidden sm:block">
        <ComplianceRecentBox />
      </div>
    </div>
  );
};

export default Dashboard;